import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    TextField,
    Typography,
    Chip
} from "@mui/material";

import { USER_ID } from "../../../Constants/StorageConstants";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

import AxiosInstance from "../../../services/AxiosInstance";
import { useAlert } from "../../alerts/AlertContext";
import { CHANGE_REQUEST_URL } from "../../../Constants/Constants";
import { ThemeProvider } from "../../v2/home/theme/ThemeProvider";

const ApprovalsTab = ({ change }) => {

    const showAlert = useAlert();

    const [approvals, setApprovals] = useState([]);

    const [loading, setLoading] = useState(false);

    const [saving, setSaving] = useState(false);

    const [approvalDialogOpen, setApprovalDialogOpen] =
        useState(false);

    const [selectedApproval, setSelectedApproval] =
        useState(null);

    const [action, setAction] =
        useState("");

    const [comments, setComments] =
        useState("");


    /*
     * Load approvals
     */
    const loadApprovals = async () => {

        if (!change.id) {
            return;
        }

        setLoading(true);

        try {

            const response =
                await AxiosInstance.get(
                    `${CHANGE_REQUEST_URL}/${change.id}/approvals`
                );

            setApprovals(
                response?.data || []
            );

        } catch (error) {

            console.error(
                "Unable to load approvals",
                error
            );

            showAlert(
                "Unable to load approvals",
                "error"
            );

        } finally {

            setLoading(false);

        }
    };


    useEffect(() => {

        loadApprovals();

    }, [change.id]);


    /*
     * Open approval dialog
     */
    const openApprovalDialog = (
        approval,
        approvalAction
    ) => {

        setSelectedApproval(approval);

        setAction(approvalAction);

        setComments("");

        setApprovalDialogOpen(true);
    };


    /*
     * Close approval dialog
     */
    const closeApprovalDialog = () => {

        if (saving) {
            return;
        }

        setApprovalDialogOpen(false);

        setSelectedApproval(null);

        setAction("");

        setComments("");
    };


    /*
     * Submit approval
     */
    const handleApproval = async () => {

        if (!selectedApproval) {
            return;
        }

        /*
         * Reject requires comment
         */
        if (
            action === "REJECT" &&
            !comments.trim()
        ) {

            showAlert(
                "Please enter rejection reason",
                "error"
            );

            return;
        }

        setSaving(true);

        try {

            const payload = {

                action: action,

                comments:
                    comments.trim()

            };

            await AxiosInstance.post(
                `${CHANGE_REQUEST_URL}/${change.id}/approvals/${selectedApproval.id}`,
                payload
            );

            showAlert(
                action === "APPROVE"
                    ? "Change approved successfully"
                    : "Change rejected successfully",
                "success"
            );

            closeApprovalDialog();

            await loadApprovals();

        } catch (error) {

            console.error(
                "Unable to process approval",
                error
            );

            showAlert(
                error?.response?.data?.message ||
                "Unable to process approval",
                "error"
            );

        } finally {

            setSaving(false);
        }
    };


    if (loading) {

        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 5
                }}
            >
                <CircularProgress />
            </Box>
        );
    }


    return (
        <>
            <Stack spacing={2}>

                {approvals.length === 0 ? (

                    <Paper
                        variant="outlined"
                        sx={{ p: 3 }}
                    >
                        <Typography
                            color="text.secondary"
                        >
                            No approval requests found.
                        </Typography>
                    </Paper>

                ) : (

                    approvals.map((approval) => (

                        <Paper
                            key={approval.id}
                            variant="outlined"
                            sx={{ p: 2 }}
                        >

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent:
                                        "space-between",
                                    alignItems: "center",
                                    gap: 2
                                }}
                            >

                                <Box>

                                    <Typography
                                        variant="body1"
                                        fontWeight={600}
                                    >
                                        {
                                            approval.approverName ||
                                            "-"
                                        }
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {
                                            approval.roleName ||
                                            "Approver"
                                        }
                                    </Typography>

                                </Box>


                                <Chip
                                    label={
                                        approval.status ||
                                        "PENDING"
                                    }
                                    size="small"
                                />

                            </Box>


                            {approval.comments && (

                                <Typography
                                    variant="body2"
                                    sx={{ mt: 2 }}
                                >
                                    {approval.comments}
                                </Typography>

                            )}


                            {(
                                approval.status === "PENDING" ||
                                !approval.status
                            ) && (

                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        justifyContent="flex-end"
                                        sx={{ mt: 2 }}
                                    >

                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            startIcon={
                                                <CancelOutlinedIcon />
                                            }
                                            onClick={() =>
                                                openApprovalDialog(
                                                    approval,
                                                    "REJECT"
                                                )
                                            }
                                        >
                                            Reject
                                        </Button>

                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            startIcon={
                                                <CheckCircleOutlineIcon />
                                            }
                                            onClick={() =>
                                                openApprovalDialog(
                                                    approval,
                                                    "APPROVE"
                                                )
                                            }
                                            disabled={localStorage.getItem(USER_ID) !== approval.approverId}
                                        >
                                            Approve
                                        </Button>

                                    </Stack>

                                )}

                        </Paper>

                    ))

                )}

            </Stack>


            {/* Approval confirmation dialog */}

            <Dialog
                open={approvalDialogOpen}
                onClose={closeApprovalDialog}
                fullWidth
                maxWidth="sm"
            >

                <DialogTitle>
                    {action === "APPROVE"
                        ? "Approve Change"
                        : "Reject Change"}
                </DialogTitle>


                <DialogContent dividers>

                    <Stack spacing={2}>

                        <Typography>
                            Are you sure you want to{" "}
                            <strong>
                                {action === "APPROVE"
                                    ? "approve"
                                    : "reject"}
                            </strong>{" "}
                            this change?
                        </Typography>


                        {selectedApproval && (

                            <Paper
                                variant="outlined"
                                sx={{ p: 2 }}
                            >

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Approver
                                </Typography>

                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                >
                                    {
                                        selectedApproval.approverName
                                    }
                                </Typography>

                            </Paper>

                        )}
                        <ThemeProvider>

                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label={
                                    action === "REJECT"
                                        ? "Rejection Reason"
                                        : "Comments"
                                }
                                required={
                                    action === "REJECT"
                                }
                                value={comments}
                                onChange={(e) =>
                                    setComments(
                                        e.target.value
                                    )
                                }
                            />
                        </ThemeProvider>
                    </Stack>

                </DialogContent>


                <DialogActions>

                    <Button
                        onClick={closeApprovalDialog}
                        disabled={saving}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color={
                            action === "APPROVE"
                                ? "success"
                                : "error"
                        }
                        onClick={handleApproval}
                        disabled={saving}
                    >
                        {saving
                            ? "Processing..."
                            : action === "APPROVE"
                                ? "Approve"
                                : "Reject"}
                    </Button>

                </DialogActions>

            </Dialog>
        </>
    );
};

export default ApprovalsTab;